/**
 * Préférence personnelle TiTiler (localStorage) : userId → resortId → layerId (id geoserverinfos).
 * Le défaut pour tout le site est stocké côté API (`geoserverinfos.titiler_style`) ;
 */

import { isSnowDepthTitilerLayer } from './titilerTileUrl.js'
import { getTitilerDefaultsForRasterType } from './titilerRasterDefaults.js'

const STORAGE_KEY = 'elda.layerTitilerStyles.v1'

/** Render modes accepted in phase 1 (aspect reserved for phase 2). */
const ALLOWED_RENDER_MODES = new Set(['default', 'hillshade', 'slope', 'contours'])

export const TITILER_STYLE_CHANGED_EVENT = 'titiler-layer-style-changed'

/**
 * @typedef {object} LayerTitilerStyle
 * @property {string} colormapName
 * @property {'auto'|'manual'} rescaleMode
 * @property {string} [rescaleManual] min,max pour TiTiler rescale
 */

function readAll() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return typeof parsed === 'object' && parsed !== null ? parsed : {}
  } catch {
    return {}
  }
}

function writeAll(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  } catch (e) {
    console.warn('[layerTitilerStyleStorage] write failed', e)
  }
}

/**
 * Style from API (geoserverinfos.titiler_style).
 */
export function normalizeTitilerStylePayload(raw) {
  if (raw == null) return null

  let obj = raw
  if (typeof raw === 'string') {
    try {
      obj = JSON.parse(raw)
    } catch {
      return null
    }
  }

  if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
    return null
  }

  const name = obj.colormapName ?? obj.colormap_name
  if (typeof name !== 'string') return null
  const colormapName = name.trim()
  if (!colormapName) return null

  const isManual = obj.rescaleMode === 'manual' || obj.rescale_mode === 'manual'

  const result = {
    colormapName,
    rescaleMode: isManual ? 'manual' : 'auto',
  }

  if (isManual) {
    const manualSrc = obj.rescaleManual ?? obj.rescale_manual
    if (manualSrc != null) {
      const rescaleManual = String(manualSrc).trim()
      if (rescaleManual) result.rescaleManual = rescaleManual
    }
  }

  const renderModeRaw = String(obj.renderMode ?? obj.render_mode ?? '').trim()
  if (renderModeRaw && ALLOWED_RENDER_MODES.has(renderModeRaw)) {
    result.renderMode = renderModeRaw
  }
  const rp = obj.renderParams ?? obj.render_params
  if (rp && typeof rp === 'object' && !Array.isArray(rp)) {
    result.renderParams = { ...rp }
  }

  const opacityRaw = obj.opacity ?? obj.raster_opacity
  if (opacityRaw != null) {
    const op = Number(opacityRaw)
    if (Number.isFinite(op) && op >= 0 && op <= 1) {
      result.opacity = op
    }
  }

  return result
}

/**
 * Effective preference: personal (localStorage) then site default (API) then null → .env / stats.
 */
export function getResolvedTitilerStyleForLayer(layer, userId, resortId) {
  const personal = getTitilerStyleForLayer(userId, resortId, layer?.id)
  if (personal) return personal
  const fromApi = normalizeTitilerStylePayload(layer?.titiler_style)
  if (fromApi) return fromApi
  //défaut Neige ELDA si l’API n’a pas encore titiler_style
  if (layer && isSnowDepthTitilerLayer(layer)) {
    return {
      colormapName:
        import.meta.env.VITE_TITILER_SNOW_COLORMAP?.trim() || 'elda_snow_mnt',
      rescaleMode: 'auto',
    }
  }
  // Fallback per-type defaults (DTM/DSM → gist_earth, ortho_ir → viridis, etc.)
  // Skip ortho_rgb : its colormapName is null on purpose (RGB native rendering).
  if (layer?.raster_type && layer.raster_type !== 'ortho_rgb') {
    const d = getTitilerDefaultsForRasterType(layer.raster_type)
    if (d?.colormapName) {
      return {
        colormapName: d.colormapName,
        rescaleMode: d.rescaleMode || 'auto',
        renderMode: d.renderMode || 'default',
      }
    }
  }
  return null
}

/**
 * @param {number|string|null|undefined} userId
 * @param {number|string|null|undefined} resortId
 * @param {number|string|null|undefined} layerId
 * @returns {LayerTitilerStyle | null}
 */
export function getTitilerStyleForLayer(userId, resortId, layerId) {
  if (userId == null || resortId == null || layerId == null) return null
  const all = readAll()
  const u = all[String(userId)]
  if (!u || typeof u !== 'object') return null
  const r = u[String(resortId)]
  if (!r || typeof r !== 'object') return null
  const s = r[String(layerId)]
  if (!s || typeof s !== 'object' || !s.colormapName) return null
  return {
    colormapName: String(s.colormapName),
    rescaleMode: s.rescaleMode === 'manual' ? 'manual' : 'auto',
    rescaleManual:
      s.rescaleManual != null ? String(s.rescaleManual).trim() : undefined,
    ...(s.renderMode && ALLOWED_RENDER_MODES.has(s.renderMode)
      ? { renderMode: s.renderMode }
      : {}),
    ...(s.renderParams && typeof s.renderParams === 'object'
      ? { renderParams: { ...s.renderParams } }
      : {}),
    ...(typeof s.opacity === 'number' && s.opacity >= 0 && s.opacity <= 1
      ? { opacity: s.opacity }
      : {}),
  }
}

export function setTitilerStyleForLayer(userId, resortId, layerId, style) {
  const all = readAll()
  const uid = String(userId)
  const rid = String(resortId)
  const lid = String(layerId)
  if (!all[uid]) all[uid] = {}
  if (!all[uid][rid]) all[uid][rid] = {}
  all[uid][rid][lid] = {
    colormapName: style.colormapName,
    rescaleMode: style.rescaleMode,
    ...(style.rescaleManual ? { rescaleManual: style.rescaleManual } : {}),
    ...(style.renderMode && ALLOWED_RENDER_MODES.has(style.renderMode)
      ? { renderMode: style.renderMode }
      : {}),
    ...(style.renderParams && typeof style.renderParams === 'object'
      ? { renderParams: { ...style.renderParams } }
      : {}),
    ...(typeof style.opacity === 'number' && style.opacity >= 0 && style.opacity <= 1
      ? { opacity: style.opacity }
      : {}),
  }
  writeAll(all)
  window.dispatchEvent(new CustomEvent(TITILER_STYLE_CHANGED_EVENT))
}

/**
 * removes user preference → returns to site default (.env / stats TiTiler)
 */
export function clearTitilerStyleForLayer(userId, resortId, layerId) {
  const all = readAll()
  const uid = String(userId)
  const rid = String(resortId)
  const lid = String(layerId)
  if (all[uid]?.[rid]?.[lid] != null) {
    delete all[uid][rid][lid]
    if (Object.keys(all[uid][rid]).length === 0) delete all[uid][rid]
    if (Object.keys(all[uid]).length === 0) delete all[uid]
    writeAll(all)
    window.dispatchEvent(new CustomEvent(TITILER_STYLE_CHANGED_EVENT))
  }
}

/**
 * reads user from the same format as `authApi` (payload { user, permissions }).
 */
export function getStationSnowStyleDefaults() {
  return {
    colormapName:
      import.meta.env.VITE_TITILER_SNOW_COLORMAP?.trim() || 'elda_snow_mnt',
    rescaleEnv: import.meta.env.VITE_TITILER_SNOW_RESCALE?.trim() || '',
  }
}

export function getUserContextFromStorage() {
  try {
    const raw = localStorage.getItem('user')
    if (!raw) return { userId: null, resortId: null }
    const p = JSON.parse(raw)
    const u = p?.user
    return {
      userId: u?.id ?? null,
      resortId: u?.resort_id ?? null,
    }
  } catch {
    return { userId: null, resortId: null }
  }
}
